// Library Imports
import axios from 'axios';
import Cookies from 'js-cookie';

// Local Imports
import { wordpress_url } from '@/redux/constant';
import { persistor, store } from '@/redux/store';
import { logout, logoutUserWpAPIFn, updateCurrentUser } from '@/redux/features/CurrentUser/currentUserSlice';
import { resetMediaKitState } from '@/redux/features/MediaKit/mediakitSlice';
import { setConnectionStatus } from '@/redux/features/InternetConnect/InternetConnectSlice';
import { customToast } from '@/utils';

const axiosInstance = axios.create();

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second


const isRetryableError = (error) => {
  if (!error.response) {
    // Network error
    return true;
  }
  const status = error.response.status;
  return status >= 500 && status < 600;
};


const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));


const isWordPressRequest = (config) => {
  if (!config?.url) return false;
  const base = config.baseURL || '';
  const url = config.url;
  const fullUrl = url.startsWith('http') ? url : (base.replace(/\/$/, '') + (url.startsWith('/') ? url : '/' + url));
  return fullUrl.startsWith(wordpress_url);
};


axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from store if available
    const state = store.getState();
    let token = state?.currentUser?.currentUser?.accessToken;
    
    // If no token in store, check cookie as fallback
    if (!token && typeof window !== 'undefined') {
      const wpToken = Cookies.get("_wp_knock");
      if (wpToken) {
        try {
          const loginData = JSON.parse(atob(wpToken));
          const cookieToken = loginData?.currentUser?.accessToken;
          
          // Restore token to store if found in cookie
          if (cookieToken) {
            store.dispatch(updateCurrentUser({
              accessToken: loginData?.currentUser?.accessToken,
              email: loginData?.currentUser?.email,
              userRole: loginData?.currentUser?.userRole,
              username: loginData?.currentUser?.username,
              refreshToken: loginData?.currentUser?.refreshToken,
              userId: loginData?.currentUser?.userId,
              isInstagramAuthentcated: loginData?.currentUser?.isInstagramAuthentcated,
              emailConfirmed: loginData?.currentUser?.emailConfirmed,
            }));
            setAuthToken(cookieToken);
            token = cookieToken;
          }
        } catch (error) {
          // Silently handle malformed cookies - fallback to existing behavior
          console.error('Error parsing cookie token:', error);
        }
      }
    }
    
    if (token && !isWordPressRequest(config)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Update connection status
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      store.dispatch(setConnectionStatus(false));
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


axiosInstance.interceptors.response.use(
  (response) => {
    // Update connection status on successful response
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      store.dispatch(setConnectionStatus(true));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const dispatch = store.dispatch;

    // Handle offline status
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      dispatch(setConnectionStatus(false));
      return Promise.reject(error);
    }

    // Update connection status if we get a response (we're online)
    if (error.response) {
      dispatch(setConnectionStatus(true));
    }

    // Handle 401 Unauthorized - try to restore from cookie first
    if (error?.response?.status === 401) {
      // Do not logout when 401 is from WordPress API
      if (isWordPressRequest(originalRequest)) {
        return Promise.reject(error);
      }
      // Check if we can restore token from cookie before logging out
      if (typeof window !== 'undefined' && !originalRequest._tokenRestored) {
        const wpToken = Cookies.get("_wp_knock");
        if (wpToken) {
          try {
            const loginData = JSON.parse(atob(wpToken));
            const cookieToken = loginData?.currentUser?.accessToken;
            const usedToken = originalRequest.headers?.Authorization?.replace('Bearer ', '');
            
            // If cookie token exists and differs from what was used, restore it
            if (cookieToken && cookieToken !== usedToken) {
              // Restore user data from cookie to Redux store
              dispatch(updateCurrentUser({
                accessToken: loginData?.currentUser?.accessToken,
                email: loginData?.currentUser?.email,
                userRole: loginData?.currentUser?.userRole,
                username: loginData?.currentUser?.username,
                refreshToken: loginData?.currentUser?.refreshToken,
                userId: loginData?.currentUser?.userId,
                isInstagramAuthentcated: loginData?.currentUser?.isInstagramAuthentcated,
                emailConfirmed: loginData?.currentUser?.emailConfirmed,
              }));
              setAuthToken(cookieToken);
              
              // Retry original request with restored token
              originalRequest._tokenRestored = true;
              originalRequest.headers.Authorization = `Bearer ${cookieToken}`;
              return axiosInstance(originalRequest);
            }
          } catch (error) {
            // Silently handle malformed cookies - proceed to logout
            console.error('Error restoring token from cookie:', error);
          }
        }
      }
      
      // Only logout if no valid cookie token found or token restoration failed
      dispatch(logout());
      dispatch(resetMediaKitState());
      dispatch(logoutUserWpAPIFn());
      await persistor.flush();
      customToast.error('Your session has expired. Please log in again.');
      return Promise.reject(error);
    }

    // Handle 500 Server Error
    if (error?.response?.status === 500) {
      customToast.error('Oops. It looks like something went wrong. Please try again or contact support.');
      return Promise.reject(error);
    }

    // Handle 403 Forbidden
    if (error?.response?.status === 403) {
      customToast.error('You do not have permission to perform this action.');
      return Promise.reject(error);
    }

    // Handle 404 Not Found
    if (error?.response?.status === 404) {
      customToast.error('The requested resource was not found.');
      return Promise.reject(error);
    }

    // Retry logic for network errors and 5xx errors
    if (isRetryableError(error) && !originalRequest._retry) {
      originalRequest._retry = originalRequest._retry || 0;
      
      if (originalRequest._retry < MAX_RETRIES) {
        originalRequest._retry += 1;
        
        // Wait before retrying
        await delay(RETRY_DELAY * originalRequest._retry);
        
        // Retry the request
        return axiosInstance(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

export const setAuthToken = (token) => {
  if (token) {
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
};

export default axiosInstance;
